/**
 * DVL - lens × port compatibility matrix
 * ======================================
 * The shopper-facing twin of EQA's catalog Matrix: a camera rail, then a grid of the camera's
 * lenses against the ports that fit them (● flat, ● dome version, ⚙ zoom gear). Opening a lens row
 * adds what EQA has no need for — the actual parts, with photo, live price and a product link.
 * Renders into the markup of snippets/dvl-lens-compat.liquid.
 *
 * Data: one JSON file per store, exported from the EQA catalog (the graph the product pages' kit
 * builder is generated from):
 *   housings[]  {id, brand, name, gen, body[{h,t}], bundles{portCode: handle}, included, lenses[], fixed}
 *     lenses[]  {n: name, b: brand, flat[sku], dome[sku], gear[sku], gr[ring]}
 *               `gr` = the dome rings this lens has a gear for (a gear SKU can serve several lenses
 *               on different rings), so a dome cell marks ⚙ exactly as the EQA matrix does.
 *     fixed     {flat[sku], dome[sku]} for a camera with a built-in lens (RX100): ports, no grid
 *   parts{sku}  {k: flat|dome|gear, n: label, m: port model, sz, ring, mat, lc: flat-port family
 *               code, fit[], h, v}
 *   rings{code} {er: extension ring SKU, mm: its length} — named under the dome column (V1 =
 *               ER9023) and the order the dome columns run in (by ring length)
 * `h`/`v` (product handle / variant id) are present only when THIS store sells the part.
 *
 * Prices, images and stock are fetched from /products/<handle>.js the first time a row is opened,
 * so they are live and in the shopper's market currency. Each product is fetched once per page.
 *
 * State (camera, open lens) is mirrored into the URL, so a link can open the chart on a given
 * camera and lens: ?camera=A7IV&lens=Sony FE 24-70mm F2.8 GM II
 * (never `view`: that is Shopify's own template selector, ?view=<alternate template>).
 */
(function () {
  'use strict';

  var LENGTHS = { Pancake: 0, 'Super Short': 1, Short: 2, Medium: 3, Long: 4, 'Z Long': 5, 'Extra Long': 6 };

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function fill(tpl, vars) {
    return String(tpl || '').replace(/\{(\w+)\}/g, function (m, k) { return vars[k] != null ? vars[k] : m; });
  }
  // "24-70" finds "24-70mm", "2470" too: compare letters and digits only
  function norm(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function cssEsc(s) { return window.CSS && CSS.escape ? CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&'); }

  // the add control is an icon in the card's corner: a plus to add, a tick once added, a warning if
  // the add failed. currentColor, so each theme's text colour carries it.
  var SVG = '<svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" ' +
    'stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">';
  var CART = '<path d="M2.4 3.4h1.7l1 3"/><path d="M5.1 6.4h12l-1.9 6H7.1z"/>' +
    '<circle cx="8.3" cy="15.9" r="1.1"/><circle cx="14.2" cy="15.9" r="1.1"/>';
  var ICON = {
    add: SVG + CART + '<path d="M11.1 7.7v3.4M9.4 9.4h3.4"/></svg>',   // a cart with a plus in it
    done: SVG + CART + '<path d="M9.3 9.3l1.5 1.5 2.6-2.9"/></svg>',   // ... and a tick once added
    fail: SVG + '<circle cx="10" cy="10" r="7.6"/><path d="M10 6v5M10 13.6v.2"/></svg>'
  };

  function Finder(root) {
    this.root = root;
    this.T = {};
    try { this.T = JSON.parse(root.querySelector('.dvl-lc__i18n').textContent); } catch (e) {}
    var d = root.dataset;
    this.base = (d.root || '/').replace(/\/?$/, '/'); // routes.root_url has no trailing slash on /fr etc.
    this.money = null;
    try { this.money = new Intl.NumberFormat(d.locale || undefined, { style: 'currency', currency: d.currency || 'USD' }); } catch (e) {}
    this.contact = d.contact || '/pages/contact';
    this.products = {};
    this.cols = {};
    this.narrow = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };
    // phones start on the list, desktops on the chart; once the visitor picks, their choice sticks
    this.state = { camera: '', camQ: '', brand: '', q: '', gearOnly: false, open: '', auto: false,
                   view: this.narrow.matches ? 'list' : 'grid', viewPicked: false };
    this.el = {
      rail: root.querySelector('.dvl-lc__rail'),
      camQ: root.querySelector('.dvl-lc__camq'),
      brands: root.querySelector('.dvl-lc__brands'),
      nav: root.querySelector('.dvl-lc__navlist'),
      camera: root.querySelector('.dvl-lc__camera'),
      addonbox: root.querySelector('.dvl-lc__addonbox'),
      campick: root.querySelector('.dvl-lc__campick'),
      campickName: root.querySelector('.dvl-lc__campick-name'),
      backdrop: root.querySelector('.dvl-lc__backdrop'),
      title: root.querySelector('.dvl-lc__title'),
      toolbar: root.querySelector('.dvl-lc__toolbar'),
      lensQ: root.querySelector('.dvl-lc__lensq'),
      gearOnly: root.querySelector('[data-gear-only]'),
      stats: root.querySelector('.dvl-lc__stats'),
      results: root.querySelector('.dvl-lc__results'),
      notListed: root.querySelector('.dvl-lc__notlisted')
    };
    var T = this.T;
    Array.prototype.forEach.call(root.querySelectorAll('[data-t]'), function (n) { n.textContent = T[n.getAttribute('data-t')] || ''; });
    Array.prototype.forEach.call(root.querySelectorAll('.dvl-lc__searchx'), function (b) {
      b.textContent = '✕';
      b.setAttribute('aria-label', T.clear || 'Clear');
    });
    this.el.camQ.placeholder = T.filter_cameras || '';
    this.el.lensQ.placeholder = T.filter_lenses || '';
    this.load(d.src);
  }

  Finder.prototype.load = function (src) {
    var self = this;
    fetch(src, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(function (data) {
        self.data = data;
        self.byId = {};
        data.housings.forEach(function (h) { self.byId[h.id] = h; });
        self.buildCameraSelect();
        self.bind();
        self.readUrl();
        if (!self.state.camera) self.applyDefault();
        self.renderRail();
        self.renderMain(true);
      })
      .catch(function () {
        self.el.results.innerHTML = '<p class="dvl-lc__empty dvl-lc__err">' + esc(self.T.error) + '</p>';
      });
  };

  // ---- the camera rail (and its mobile twin, a <select>) -----------------------------------------

  Finder.prototype.buildCameraSelect = function () {
    var groups = {}, order = [];
    this.data.housings.forEach(function (h) {
      if (!groups[h.brand]) { groups[h.brand] = []; order.push(h.brand); }
      groups[h.brand].push(h);
    });
    this.brandList = order;
    var html = '<option value="">' + esc(this.T.camera_placeholder) + '</option>';
    order.forEach(function (b) {
      html += '<optgroup label="' + esc(b) + '">' + groups[b].map(function (h) {
        return '<option value="' + esc(h.id) + '">' + esc(h.name + (h.gen ? ' · ' + h.gen : '')) + '</option>';
      }).join('') + '</optgroup>';
    });
    this.el.camera.innerHTML = html;
    this.el.camera.disabled = false;
  };

  // Landing on the chart shows something to read: the first housing of the default brand (Sony
  // where there is one), with the rail filtered to it. Marked `auto`, so the URL stays clean until
  // the shopper picks for themselves.
  /* THIS PAGE'S CAMERA, if it has one.
   *
   * Each camera now has a page of its own — EQA writes that camera's compatibility into the body and
   * marks it `data-camera`. The chart reads it from the page's own content, so a camera page needs no
   * template, no section setting and no Liquid change: drop the text in and the matrix opens on it. */
  Finder.prototype.pageCamera = function () {
    var el = document.querySelector('.dvl-lc-text[data-camera]');
    var id = el && el.getAttribute('data-camera');
    return id && this.byId[id] ? id : '';
  };

  /* The heading and the standfirst belong to the SECTION, set once per theme in the editor, so on a
     camera's page they still said "Choose your camera…" — the index's words on a page that offers no
     choice. They are named after the camera here instead. Both are optional: a theme that has neither
     simply keeps what it has, and nothing depends on finding them. */
  Finder.prototype.nameThePage = function (h) {
    if (!h) return;
    var host = this.root.closest('.shopify-section') || this.root.parentNode;
    if (!host) return;
    var title = h.name + (h.gen ? ' ' + h.gen : '');
    var head = host.querySelector('h1');
    if (head && head.textContent.indexOf(title) < 0) {
      head.textContent = fill(this.T.single_title || '{camera} — {what}', {
        camera: title,
        what: head.textContent.trim(),
      });
    }
    // the standfirst is the theme's own rich text, immediately before the chart
    var intro = host.querySelector('.rte, .dvl-lc__intro');
    if (intro && !intro.contains(this.root)) {
      intro.textContent = fill(this.T.single_intro || 'Every lens the {camera} housing supports, and the port each one needs.', {
        camera: title,
      });
    }
  };

  Finder.prototype.applyDefault = function () {
    var own = this.pageCamera();
    if (own) {
      this.state.brand = (this.byId[own] || {}).brand || '';
      this.state.camera = own;
      this.state.auto = true; // the page already says which camera; the URL need not repeat it
      this.el.camera.value = own;
      /* A camera's page is ABOUT that camera: no rail, no camera picker, the table across the full
         width. With them it was the same chart at a different address, which is not a page about a
         camera — it is the index wearing a different handle. The way to the others is the header's
         "All cameras". */
      this.root.classList.add('is-single');
      this.nameThePage(this.byId[own]);
      return;
    }
    var brands = this.brandList || [];
    var brand = brands.indexOf('Sony') >= 0 ? 'Sony' : brands[0];
    var first = this.data.housings.filter(function (h) { return !brand || h.brand === brand; })[0];
    if (!first) return;
    this.state.brand = brand || '';
    this.state.camera = first.id;
    this.state.auto = true;
    this.el.camera.value = first.id;
  };

  /* THE RAIL LISTS CAMERAS, NOT HOUSINGS.
   *
   * It listed our 41 housings, which is a grouping of ours rather than a thing anyone owns: `A6XXX`
   * hid the A6000, A6100, A6300, A6400 and A6500, `RX1XX` five RX100s — 54 models in all, thirteen of
   * them unfindable by name. It runs the other way too: A7IV has three housings (standard, GEN2, NG)
   * and A7RIII two, so a camera can need a housing chosen after it.
   *
   * `cameras()` is that list, built once: one entry per model, carrying the housings that serve it. */
  Finder.prototype.cameras = function () {
    if (this._cams) return this._cams;
    var by = {}, order = [];
    this.data.housings.forEach(function (h) {
      (h.cams && h.cams.length ? h.cams : [h.name]).forEach(function (c) {
        var key = h.brand + ' ' + c;
        if (!by[key]) { by[key] = { key: key, cam: c, brand: h.brand, housings: [] }; order.push(key); }
        by[key].housings.push(h);
      });
    });
    this._cams = order.map(function (k) { return by[k]; });
    this._cams.sort(function (a, b) {
      return a.brand.localeCompare(b.brand) || a.cam.localeCompare(b.cam, undefined, { numeric: true });
    });
    return this._cams;
  };

  /** The camera entry the open housing is being shown under. */
  Finder.prototype.currentCam = function () {
    var s = this.state, list = this.cameras();
    var hit = s.model && list.filter(function (c) { return c.key === s.model; })[0];
    if (hit) return hit;
    return list.filter(function (c) {
      return c.housings.some(function (h) { return h.id === s.camera; });
    })[0] || null;
  };

  /** Pick a camera: keep the housing already open if it serves this camera, else its first. */
  Finder.prototype.pickModel = function (key) {
    var s = this.state;
    var cam = this.cameras().filter(function (c) { return c.key === key; })[0];
    if (!cam) return;
    s.model = key;
    var keep = cam.housings.filter(function (h) { return h.id === s.camera; })[0];
    this.pick((keep || cam.housings[0]).id);
  };

  /* Which housing, when the camera has more than one — named by the catalogue ("Salted Line CH A7IV
     GEN2"), because that is what the shopper owns or is about to buy, where "A7IV GEN2" is our code
     for it. Under the housing title, since it changes the table beneath. */
  Finder.prototype.housingPicker = function () {
    var here = this.currentCam(), s = this.state, T = this.T;
    if (!here || here.housings.length < 2) return '';
    return '<div class="dvl-lc__bodies"><span class="dvl-lc__bodylabel">' +
      esc(T.which_housing || 'Which housing do you have?') + '</span>' +
      here.housings.map(function (h) {
        var on = h.id === s.camera;
        return '<button type="button" class="dvl-lc__body' + (on ? ' is-on' : '') + '" data-housing="' + esc(h.id) + '"' +
          (on ? ' aria-pressed="true"' : '') + '>' + esc(h.label || h.name) + '</button>';
      }).join('') + '</div>';
  };

  Finder.prototype.renderRail = function () {
    var T = this.T, s = this.state;
    this.el.brands.innerHTML = this.brandList.length > 1
      ? [''].concat(this.brandList).map(function (b) {
          var on = s.brand === b;
          return '<button type="button" class="dvl-lc__filter' + (on ? ' is-on' : '') + '" data-brand="' + esc(b) +
            '" aria-pressed="' + (on ? 'true' : 'false') + '">' + esc(b || T.all_brands) + '</button>';
        }).join('')
      : '';

    var q = norm(s.camQ), here = this.currentCam();
    var rows = this.cameras().filter(function (c) {
      if (s.brand && c.brand !== s.brand) return false;
      if (!q) return true;
      return norm(c.key + c.housings.map(function (h) { return h.name + h.gen + h.id + (h.label || ''); }).join('')).indexOf(q) >= 0;
    });
    this.el.nav.innerHTML = rows.length
      ? rows.map(function (c) {
          var h = c.housings[0];
          var n = h.fixed ? h.fixed.flat.length + h.fixed.dome.length : h.lenses.length;
          var on = here && here.key === c.key;
          // a camera with several housings says so: the choice is the next thing it will ask
          var sub = c.housings.length > 1 ? fill(T.n_housings || '{n} housings', { n: c.housings.length }) : c.brand;
          return '<button type="button" class="dvl-lc__nav' + (on ? ' is-on' : '') + '" data-model="' + esc(c.key) + '"' +
            ' title="' + esc(c.key) + '">' +
            '<span class="dvl-lc__navrow"><b>' + esc(c.brand + ' ' + c.cam) + '</b><span class="dvl-lc__navn">(' + n + ')</span></span>' +
            '<span class="dvl-lc__navdim">' + esc(sub) + '</span></button>';
        }).join('')
      : '<div class="dvl-lc__empty">' + esc(fill(T.none, { q: s.camQ })) + '</div>';
    this.el.camQ.parentNode.querySelector('.dvl-lc__searchx').hidden = !s.camQ;

    if (this.el.campickName) this.el.campickName.textContent = here ? here.key : T.camera_placeholder;
  };

  Finder.prototype.bind = function () {
    var self = this, camTimer = null, lensTimer = null;

    this.el.camera.addEventListener('change', function () { self.pick(self.el.camera.value); });
    this.el.camQ.addEventListener('input', function () {
      clearTimeout(camTimer);
      camTimer = setTimeout(function () { self.state.camQ = self.el.camQ.value; self.renderRail(); }, 120);
    });
    this.el.lensQ.addEventListener('input', function () {
      clearTimeout(lensTimer);
      lensTimer = setTimeout(function () { self.state.q = self.el.lensQ.value; self.renderMain(); }, 120);
    });

    this.root.addEventListener('click', function (e) {
      var clear = e.target.closest('.dvl-lc__searchx');
      if (clear) {
        if (clear.getAttribute('data-clear') === 'cam') { self.state.camQ = ''; self.el.camQ.value = ''; self.renderRail(); }
        else { self.state.q = ''; self.el.lensQ.value = ''; self.renderMain(); }
        return;
      }
      var brand = e.target.closest('[data-brand]');
      if (brand) { self.state.brand = brand.getAttribute('data-brand'); self.renderRail(); return; }
      var copy = e.target.closest('[data-copy]');
      if (copy) { self.copy(copy); return; }
      var body = e.target.closest('[data-body]');
      if (body) { self.pickBody(self.current(), body.getAttribute('data-body')); return; }
      if (e.target.closest('[data-camera-drawer]')) { self.drawer(true); return; }
      if (e.target.closest('[data-close-drawer]')) { self.drawer(false); return; }
      var model = e.target.closest('[data-model]');
      if (model) { self.pickModel(model.getAttribute('data-model')); self.drawer(false); return; }
      var housing = e.target.closest('[data-housing]');
      if (housing) { self.pick(housing.getAttribute('data-housing')); return; }
      var nav = e.target.closest('[data-camera]');
      if (nav) { self.pick(nav.getAttribute('data-camera')); self.drawer(false); return; }
      if (e.target.closest('[data-gear-only]')) { self.state.gearOnly = !self.state.gearOnly; self.renderMain(); return; }
      var view = e.target.closest('[data-view]');
      if (view) {
        self.state.view = view.getAttribute('data-view');
        self.state.viewPicked = true;
        self.renderMain();
        return;
      }
      var add = e.target.closest('[data-add]');
      if (add) { e.preventDefault(); self.addToCart(add); return; }
      if (e.target.closest('.dvl-lc__close')) { self.closePanel(); self.renderAddons(); self.writeUrl(); return; }
      var lrow = e.target.closest('.dvl-lc__lhead');
      if (lrow) { self.toggleRow(lrow.closest('.dvl-lc__lrow').getAttribute('data-lens')); return; }
      var row = e.target.closest('tr[data-lens]');
      if (row && !e.target.closest('a')) self.toggleRow(row.getAttribute('data-lens'));
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && self.root.classList.contains('is-drawer')) self.drawer(false);
    });

    // crosshair: highlight the hovered column, as the EQA matrix does. The hovered ROW is lit by
    // hand, because its two halves now live in different tables.
    this.el.results.addEventListener('mouseover', function (e) {
      var cell = e.target.closest('[data-col]');
      self.highlight(cell ? cell.getAttribute('data-col') : null);
      var row = e.target.closest('tr[data-lens]');
      self.hoverRow(row ? row.getAttribute('data-lens') : null);
    });
    this.el.results.addEventListener('mouseleave', function () { self.highlight(null); self.hoverRow(null); });
    window.addEventListener('resize', function () { self.sizeDetail(); self.syncGrid(); });
  };

  Finder.prototype.highlight = function (col) {
    if (this.hlCol === col) return;
    this.hlCol = col;
    Array.prototype.forEach.call(this.el.results.querySelectorAll('.is-col'), function (n) { n.classList.remove('is-col'); });
    if (col == null) return;
    Array.prototype.forEach.call(this.el.results.querySelectorAll('[data-col="' + col + '"]'), function (n) { n.classList.add('is-col'); });
  };

  // A copy of the header rows, in the same grid cell as the panes, sticky to the top of the viewport.
  // Column widths are copied from the live table, so the copy lines up exactly.
  Finder.prototype.buildFloatHead = function (grid, lensPane, ports) {
    var head = grid.querySelector('.dvl-lc__floathead');
    if (!head) return;
    var lensTable = lensPane.querySelector('table'), portTable = ports.querySelector('table');
    // Widths come from a <colgroup> measured off the real column-header row: with table-layout
    // fixed, per-cell widths would be read from the first row, whose group cells span many columns.
    var copy = function (table) {
      var t = document.createElement('table');
      t.className = table.className;
      t.style.width = table.getBoundingClientRect().width + 'px';
      t.style.tableLayout = 'fixed';
      var cols = table.tHead.rows[1].cells;
      var group = document.createElement('colgroup');
      for (var i = 0; i < cols.length; i++) {
        var col = document.createElement('col');
        col.style.width = cols[i].getBoundingClientRect().width + 'px';
        group.appendChild(col);
      }
      t.appendChild(group);
      t.appendChild(table.tHead.cloneNode(true));
      Array.prototype.forEach.call(t.querySelectorAll('th'), function (th) {
        th.style.top = ''; // the copy itself is the sticky element now
        th.style.boxSizing = 'border-box';
      });
      return t;
    };
    head.innerHTML = '';
    var left = document.createElement('div');
    left.className = 'dvl-lc__floatlens';
    left.appendChild(copy(lensTable));
    var right = document.createElement('div');
    right.className = 'dvl-lc__floatports';
    var inner = document.createElement('div');
    inner.className = 'dvl-lc__floatinner';
    inner.appendChild(copy(portTable));
    right.appendChild(inner);
    head.appendChild(left);
    head.appendChild(right);
    head.style.setProperty('--dvl-lc-floatw', lensTable.getBoundingClientRect().width + 'px');
    // Layout set here as well as in the stylesheet: a browser holding an older cached stylesheet
    // would otherwise treat this as an ordinary grid item and push the panes into the wrong cells.
    var lay = { position: 'sticky', gridArea: '1 / 1 / 2 / 3', zIndex: '3', display: 'grid',
                gridTemplateColumns: 'var(--dvl-lc-floatw, 15em) minmax(0, 1fr)', alignSelf: 'start',
                overflow: 'hidden', pointerEvents: 'none' };
    for (var k in lay) head.style[k] = lay[k];
    lensPane.style.gridArea = '1 / 1';
    ports.style.gridArea = '1 / 2';
    this.trackFloatHead();
  };

  // Themes pin their own header to the top of the window; the floating header has to start below it.
  // There is no common name for them (<nav id="nav">, <nav id="navbarStickyDesktop">, <div
  // id="shopify-section-header">), and one of them is not hit-testable, so look both ways: whatever
  // sits at the top edge, plus any pinned nav/header-ish element. Throttled — it is called on scroll.
  Finder.prototype.headerOffset = function () {
    var now = Date.now();
    if (this._offAt && now - this._offAt < 300) return this._off || 0;
    var best = 0;
    var consider = function (n) {
      var cs = getComputedStyle(n);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      var r = n.getBoundingClientRect();
      // a bar pinned across the top, not a full-height drawer or overlay
      if (r.height > 0 && r.height < 200 && r.top <= 1 && r.width > window.innerWidth * 0.5 && r.bottom > best) best = r.bottom;
    };
    [0.5, 0.2].forEach(function (x) {
      var els = document.elementsFromPoint ? document.elementsFromPoint(Math.round(window.innerWidth * x), 2) : [];
      Array.prototype.forEach.call(els, consider);
    });
    Array.prototype.forEach.call(document.querySelectorAll('header, nav, [id*="nav" i], [id*="header" i]'), consider);
    this._off = best;
    this._offAt = now;
    return best;
  };

  // Show it only where it helps — once the real header has scrolled past, and while rows remain —
  // and keep it level with the ports pane's horizontal scroll.
  Finder.prototype.trackFloatHead = function () {
    var grid = this.el.results.querySelector('.dvl-lc__grid');
    if (!grid) return;
    var head = grid.querySelector('.dvl-lc__floathead'), ports = grid.querySelector('.dvl-lc__ports');
    var real = ports && ports.querySelector('thead');
    if (!head || !real) return;
    var off = this.headerOffset();
    var r = real.getBoundingClientRect(), g = grid.getBoundingClientRect();
    // the top scrollbar pins under the theme's header and the floating header pins under it; past the
    // chart's end it steps aside rather than hang over whatever follows
    var xbar = this.el.results.querySelector('.dvl-lc__xbar');
    if (xbar && !xbar.hidden) {
      xbar.style.top = off + 'px';
      xbar.style.visibility = g.bottom > off + xbar.offsetHeight + 40 ? '' : 'hidden';
      off += xbar.offsetHeight;
    }
    head.style.top = off + 'px';
    head.hidden = !(r.bottom < off && g.bottom > off + r.height + 8);
    var inner = head.querySelector('.dvl-lc__floatinner');
    if (inner) inner.style.transform = 'translateX(' + -ports.scrollLeft + 'px)';
  };

  // Drag the divider to widen the lens column (EQA has the same grip on its "Lens \ Port" corner);
  // double-click resets it. The width lives in a custom property the lens cells and the grip read.
  Finder.prototype.wireGrip = function (grid, lensPane) {
    var grip = grid.querySelector('.dvl-lc__grip'), self = this;
    if (!grip) return;
    var min = 90, max = 620, startX = 0, startW = 0, dragging = false;
    var set = function (px) {
      grid.style.setProperty('--dvl-lc-lensw', Math.max(min, Math.min(max, px)) + 'px');
      self.sizeDetail();
      self.syncGrid();
    };
    grip.addEventListener('pointerdown', function (e) {
      dragging = true;
      startX = e.clientX;
      startW = lensPane.getBoundingClientRect().width;
      grip.setPointerCapture(e.pointerId);
      document.body.classList.add('dvl-lc-resizing');
      e.preventDefault();
    });
    grip.addEventListener('pointermove', function (e) {
      if (dragging) set(startW + (e.clientX - startX));
    });
    var stop = function (e) {
      if (!dragging) return;
      dragging = false;
      try { grip.releasePointerCapture(e.pointerId); } catch (err) {}
      document.body.classList.remove('dvl-lc-resizing');
    };
    grip.addEventListener('pointerup', stop);
    grip.addEventListener('pointercancel', stop);
    grip.addEventListener('dblclick', function () { grid.style.removeProperty('--dvl-lc-lensw'); self.sizeDetail(); self.syncGrid(); });
    grip.addEventListener('keydown', function (e) {
      var step = e.key === 'ArrowLeft' ? -16 : e.key === 'ArrowRight' ? 16 : 0;
      if (!step) return;
      set(lensPane.getBoundingClientRect().width + step);
      e.preventDefault();
    });
  };

  // The rail doubles as the phone's camera drawer: same filter box, brand chips and counts.
  Finder.prototype.drawer = function (open) {
    var r = this.root, el = this.el;
    r.classList.toggle('is-drawer', open);
    if (el.backdrop) el.backdrop.hidden = !open;
    if (el.campick) el.campick.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.body.classList.toggle('dvl-lc-locked', open); // the page must not scroll behind it
    if (open) {
      var q = el.camQ;
      setTimeout(function () { try { q.focus({ preventScroll: true }); } catch (e) {} }, 60);
    } else if (el.campick && this.narrow.matches) {
      try { el.campick.focus({ preventScroll: true }); } catch (e) {}
    }
  };

  Finder.prototype.hoverRow = function (name) {
    if (this.hovered === name) return;
    this.hovered = name;
    Array.prototype.forEach.call(this.el.results.querySelectorAll('tr.is-hover'), function (r) { r.classList.remove('is-hover'); });
    if (!name) return;
    Array.prototype.forEach.call(this.rowsFor(name), function (r) { r.classList.add('is-hover'); });
  };

  Finder.prototype.pick = function (id) {
    // a housing picked directly (the housing chooser, or an old ?camera= link) implies its camera
    var s0 = this.state;
    var cam = this.cameras().filter(function (c) {
      return c.housings.some(function (h) { return h.id === id; }) && (!s0.model || c.key === s0.model);
    })[0] || this.cameras().filter(function (c) {
      return c.housings.some(function (h) { return h.id === id; });
    })[0];
    if (cam) this.state.model = cam.key;
    /* On a camera's own page, another camera is another PAGE: switching in place would leave the
       title, the description and the text below describing a camera the matrix is no longer showing.
       `pg` is written only for pages that exist, so this can never aim at a 404. */
    var h = this.byId[id];
    if (h && h.pg && this.pageCamera() && id !== this.pageCamera()) {
      location.href = this.base + 'pages/' + h.pg;
      return;
    }
    this.state.auto = false;
    this.state.camera = this.byId[id] ? id : '';
    this.state.q = '';
    this.state.gearOnly = false;
    this.state.open = '';
    this.el.lensQ.value = '';
    this.el.camera.value = this.state.camera;
    this.renderRail();
    this.renderMain();
  };

  // ---- URL state ----------------------------------------------------------------------------------

  Finder.prototype.readUrl = function () {
    var p = new URLSearchParams(location.search), cam = p.get('camera') || '', lens = p.get('lens') || '';
    if (this.byId[cam]) {
      this.state.camera = cam;
      this.state.open = lens;
      this.el.camera.value = cam;
      return;
    }
    // A camera page names its camera, so `?lens=` alone is enough there — which is what the link from
    // an open lens row carries. Without this it was read only alongside `?camera=`, and dropped.
    if (lens && this.pageCamera()) this.state.open = lens;
  };

  Finder.prototype.writeUrl = function () {
    try {
      var u = new URL(location.href), s = this.state, own = this.pageCamera();
      // On a camera page the URL need not repeat the camera — the page IS the camera — but it must
      // still carry the open lens, or following "this camera + this lens" would land on a bare page.
      [['camera', s.auto || own ? '' : s.camera], ['lens', s.auto && !own ? '' : s.open]].forEach(function (kv) {
        if (kv[1]) u.searchParams.set(kv[0], kv[1]); else u.searchParams.delete(kv[0]);
      });
      history.replaceState(history.state, '', u.toString());
    } catch (e) {}
  };

  // ---- products (live price / image / stock) --------------------------------------------------------

  Finder.prototype.product = function (handle) {
    if (!this.products[handle]) {
      /* null means the storefront HAS NO SUCH PAGE; undefined means the question went unanswered.
         The difference matters because a card acts on it: a 404 is grounds for removing the card,
         a flaky connection is not. */
      this.products[handle] = fetch(this.base + 'products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (r.ok) return r.json();
          return r.status === 404 || r.status === 410 ? null : undefined;
        })
        .catch(function () { return undefined; });
    }
    return this.products[handle];
  };

  Finder.prototype.url = function (handle, variant) {
    return this.base + 'products/' + encodeURIComponent(handle) + (variant ? '?variant=' + variant : '');
  };

  // A "collection" image is the composite with the option circles under the product — it is the
  // collection-grid thumbnail, not a picture of the part, and some variants carry it as their
  // variant image (seafrogs' SFFP90L6). Pick a real photo: the variant's own image unless it is a
  // composite, then a product image whose filename carries the SKU (the naming the themes scope
  // media by), then any non-composite image.
  function isComposite(url) { return /-collection[-._]/i.test(String(url || '')); }

  function partImage(product, variant) {
    var own = variant && variant.featured_image && variant.featured_image.src;
    if (own && !isComposite(own)) return own;
    var images = (product.images || []).filter(function (u) { return !isComposite(u); });
    var sku = ((variant && variant.sku) || '').toLowerCase();
    if (sku) {
      var bySku = images.filter(function (u) { return u.toLowerCase().indexOf(sku) >= 0; })[0];
      if (bySku) return bySku;
    }
    return images[0] || own || product.featured_image;
  }

  function sized(src, w) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  Finder.prototype.price = function (cents) {
    var v = cents / 100;
    return this.money ? this.money.format(v) : v.toFixed(2);
  };

  // Fill every product placeholder under `scope` once its product has loaded.
  Finder.prototype.hydrate = function (scope) {
    var self = this;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-h]:not([data-done])'), function (node) {
      node.setAttribute('data-done', '');
      self.product(node.getAttribute('data-h')).then(function (p) {
        if (!p) {
          /* THE PAGE'S LAST WORD ON WHAT IS STILL SOLD.
             The document already leaves out the products its store no longer serves, but it is built
             from a snapshot taken when it was published; a product retired since then still has a
             card here. The storefront's own answer is final, so take it: a housing or a kit is a link
             to one product and nothing else, so the card goes; a port or an accessory is a part of the
             catalogue either way, so it stays, shown as one this store does not sell -- the same
             treatment the document gives a part it cannot price. */
          if (p === null) {
            if (node.classList.contains('dvl-lc__part--housing') || node.classList.contains('dvl-lc__kit')) {
              var pair = node.parentNode;
              node.remove();
              if (pair && pair.classList.contains('dvl-lc__pair') && !pair.querySelector('.dvl-lc__kit')) {
                pair.replaceWith.apply(pair, pair.childNodes); // the port tile stands on its own again
              }
            } else {
              node.classList.add('is-na');
              var plink = node.querySelector('.dvl-lc__plink');
              if (plink) plink.removeAttribute('href');
              var pna = node.querySelector('.dvl-lc__price');
              if (pna) pna.textContent = self.T.not_sold || '';
              var padd = node.querySelector('.dvl-lc__add');
              if (padd) padd.remove();
            }
          }
          return;
        }
        var vid = node.getAttribute('data-v');
        var v = (vid && p.variants.filter(function (x) { return String(x.id) === vid; })[0]) || p.variants[0];
        var img = node.querySelector('.dvl-lc__img');
        var src = partImage(p, v);
        if (img && src) img.innerHTML = '<img src="' + esc(sized(src, 240)) + '" alt="" loading="lazy" width="120" height="120">';

        /* The colour rail: one photo per colour this kit is made in, the ones for sale only. Each
           already carries its own link; what is missing is the picture, the colour's name, and the
           knowledge of whether it can be bought — all of which arrive with the product. */
        var rail = node.querySelectorAll('.dvl-lc__swatch[data-sv]');
        if (rail.length) {
          var first = null;
          Array.prototype.forEach.call(rail, function (sw) {
            var cv = p.variants.filter(function (x) { return String(x.id) === sw.getAttribute('data-sv'); })[0];
            if (!cv || !cv.available) { sw.remove(); return; }
            if (!first) first = sw;
            var name = (cv.options && cv.options[0]) || cv.title;
            var s = partImage(p, cv);
            sw.title = name;
            sw.setAttribute('aria-label', name);
            if (s) sw.innerHTML = '<img src="' + esc(sized(s, 120)) + '" alt="' + esc(name) + '" loading="lazy" width="60" height="60">';
          });
          if (first) v = p.variants.filter(function (x) { return String(x.id) === first.getAttribute('data-sv'); })[0] || v;
          // the card's own link follows the first colour still on sale, so it never opens a sold-out one
          var main = node.querySelector('.dvl-lc__kitmain');
          if (first && main) main.setAttribute('href', first.getAttribute('href'));
          if (!first) node.classList.add('is-soldout');
        }
        var pr = node.querySelector('.dvl-lc__price');
        if (pr && !node.hasAttribute('data-included')) {
          if (node.hasAttribute('data-from')) pr.textContent = p.price_varies ? fill(self.T.from, { price: self.price(p.price_min) }) : self.price(p.price_min);
          else if (v) pr.textContent = self.price(v.price);
        }
        if (v && !v.available && !node.hasAttribute('data-included') && !rail.length) {
          node.classList.add('is-soldout');
          if (pr) pr.textContent += ' \u00b7 ' + self.T.sold_out;
          var sold = node.querySelector('.dvl-lc__add');
          if (sold) sold.remove(); // the price line already reads "· Sold out"
        }
        var title = node.querySelector('.dvl-lc__ptitle');
        if (title && !title.textContent) title.textContent = p.title;
      });
    });
  };

  // ---- the matrix ------------------------------------------------------------------------------------

  Finder.prototype.current = function () { return this.byId[this.state.camera] || null; };

  // Columns come from ALL the camera's lenses, so filtering rows never shifts them.
  Finder.prototype.columns = function (h) {
    if (this.cols[h.id]) return this.cols[h.id];
    var P = this.data.parts, flats = [], rings = [];
    h.lenses.forEach(function (l) {
      l.flat.forEach(function (s) { if (flats.indexOf(s) < 0) flats.push(s); });
      l.dome.forEach(function (s) { var r = P[s].ring; if (r && rings.indexOf(r) < 0) rings.push(r); });
    });
    var size = function (s) { return P[s].sz === '67mm' ? 0 : parseFloat(P[s].sz) || 0; };
    var len = function (s) { var m = P[s].n.match(/(Pancake|Super Short|Extra Long|Z Long|Short|Medium|Long)/); return m ? LENGTHS[m[1]] : 9; };
    flats.sort(function (a, b) { return size(a) - size(b) || len(a) - len(b); });
    // by the ring's LENGTH (V1 23mm, V7 37mm, V10 61mm, V2 88mm) — the order the ports step through;
    // a ring-less direct-mount dome (V0, W0) has no ring and leads
    var R = this.data.rings || {};
    rings.sort(function (a, b) {
      return ((R[a] || {}).mm || 0) - ((R[b] || {}).mm || 0) || a.localeCompare(b, undefined, { numeric: true });
    });
    this.cols[h.id] = { flats: flats, rings: rings };
    return this.cols[h.id];
  };

  Finder.prototype.rows = function (h) {
    var s = this.state, q = norm(s.q);
    var words = String(s.q || '').split(/\s+/).map(norm).filter(Boolean);
    return h.lenses.filter(function (l) {
      if (s.gearOnly && !l.gear.length) return false;
      if (!q) return true;
      var n = norm(l.n);
      return words.every(function (w) { return n.indexOf(w) >= 0; });
    });
  };

  Finder.prototype.renderMain = function (openFromUrl) {
    var h = this.current(), T = this.T, s = this.state, self = this;
    this.el.toolbar.hidden = !h || !!(h && h.fixed);
    this.el.notListed.hidden = !h;
    this.el.notListed.innerHTML = fill(T.not_listed, { url: esc(this.contact) });
    this.el.gearOnly.classList.toggle('is-on', s.gearOnly);
    this.el.gearOnly.setAttribute('aria-pressed', s.gearOnly ? 'true' : 'false');
    this.el.lensQ.parentNode.querySelector('.dvl-lc__searchx').hidden = !s.q;

    if (!h) {
      this.el.title.innerHTML = '';
      this.el.stats.innerHTML = '';
      this.el.results.innerHTML = '<p class="dvl-lc__empty">' + esc(T.pick_camera) + '</p>';
      this.renderAddons();
      this.writeUrl();
      return;
    }

    var inc = h.included && this.data.parts[h.included];
    /* Every camera has a page of its own, and nothing on the chart said so: the links EQA writes sit
       in the page body, inside a collapsed "as a list" disclosure, which a crawler reads and a
       shopper never opens. So the header carries it — this camera's page from the index, and the way
       back from a camera page. */
    var jump = this.pageCamera()
      ? '<a class="dvl-lc__camlink" href="' + esc(this.base) + 'pages/lens-compatibility">' +
        esc(T.all_cameras || 'All cameras') + '</a>'
      : '';
    this.el.title.innerHTML =
      '<div class="dvl-lc__titlerow"><span class="dvl-lc__titleico" aria-hidden="true">📷</span>' +
      '<b class="dvl-lc__titlecam">' + esc(h.name) + '</b>' +
      (h.gen ? '<span class="dvl-lc__titlekey">' + esc(h.gen) + '</span>' : '') + jump + '</div>' +
      this.housingPicker() +
      '<div class="dvl-lc__hbodies">' + h.body.map(function (b) {
        return '<a class="dvl-lc__part dvl-lc__part--housing" href="' + esc(self.url(b.h)) + '" data-h="' + esc(b.h) + '" data-from>' +
          '<span class="dvl-lc__img"></span><span class="dvl-lc__ptext"><span class="dvl-lc__plabel">' + esc(T.housing) + '</span>' +
          '<span class="dvl-lc__pname">' + esc(b.t) + '</span><span class="dvl-lc__price"></span></span></a>';
      }).join('') + '</div>' +
      (inc ? '<p class="dvl-lc__note">' + esc(fill(T.ships_with, { port: inc.n })) + '</p>' : '');
    this.hydrate(this.el.title);

    if (h.fixed) { // a built-in lens has no grid: just the ports that fit
      this.el.stats.innerHTML = '';
      this.el.results.innerHTML = '<p class="dvl-lc__lead">' + esc(T.fixed) + '</p>' +
        this.portGroup(h, 'flat', h.fixed.flat) + this.portGroup(h, 'dome', h.fixed.dome) +
        '<p class="dvl-lc__cartline" hidden></p>';
      this.hydrate(this.el.results);
      this.renderAddons();
      this.writeUrl();
      return;
    }

    var cols = this.columns(h), rows = this.rows(h), P = this.data.parts;
    var withGear = rows.filter(function (l) { return l.gear.length; }).length;
    var pairs = rows.reduce(function (n, l) {
      var rings = [];
      l.dome.forEach(function (sku) { if (rings.indexOf(P[sku].ring) < 0) rings.push(P[sku].ring); });
      return n + l.flat.length + rings.length;
    }, 0);
    this.el.stats.innerHTML =
      '<div class="dvl-lc__stat"><b>' + rows.length + '</b><span>' + esc(T.stat_lenses) + '</span></div>' +
      '<div class="dvl-lc__stat is-flat"><b>' + cols.flats.length + '</b><span>' + esc(T.stat_flat) + '</span></div>' +
      '<div class="dvl-lc__stat is-dome"><b>' + cols.rings.length + '</b><span>' + esc(T.stat_dome) + '</span></div>' +
      '<div class="dvl-lc__stat"><b>' + withGear + '</b><span>' + esc(T.stat_gear) + '</span></div>' +
      '<div class="dvl-lc__stat"><b>' + pairs + '</b><span>' + esc(T.stat_pairs) + '</span></div>' +
      '<span class="dvl-lc__legend"><span class="dvl-lc__lg is-flat"></span> ' + esc(T.legend_flat) +
      ' <span class="dvl-lc__lg is-dome"></span> ' + esc(T.legend_dome) +
      ' <span class="dvl-lc__zg">⚙</span> ' + esc(T.legend_gear) +
      (h && !h.fixed && h.lenses.some(function (l) { return l.nt; })
        ? ' <span class="dvl-lc__nmark" aria-hidden="true"></span> ' + esc(T.legend_note || 'see note') : '') + '</span>';

    if (!rows.length) {
      this.el.results.innerHTML = '<p class="dvl-lc__empty">' + esc(s.q ? fill(T.none, { q: s.q }) : T.no_lenses) + '</p>';
      this.renderAddons();
      this.writeUrl();
      return;
    }
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-view]'), function (c) {
      var on = c.getAttribute('data-view') === s.view;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (s.view === 'list') {
      this.el.results.innerHTML = '<p class="dvl-lc__hint">' + esc(T.row_hint) + '</p>' + this.list(h, rows);
    } else {
      this.el.results.innerHTML = '<p class="dvl-lc__hint">' + esc(T.row_hint) + '</p>' + this.table(h, cols, rows);
      this.syncGrid();
    }

    this.renderAddons();

    if (s.open) {
      // rowsFor covers both views: a <tr> in the chart, a .dvl-lc__lrow in the list. Looking only for
      // the <tr> meant a ?lens= link silently dropped its lens on phones.
      if (this.rowsFor(s.open).length) this.openRow(s.open, openFromUrl);
      else s.open = '';
    }
    this.writeUrl();
  };

  // Two tables side by side: the lens column is frozen in its own pane, the ports scroll in theirs,
  // so the horizontal scrollbar belongs to the port columns alone. syncGrid() matches the row
  // heights afterwards and ties the panes' vertical scrolling together.
  Finder.prototype.table = function (h, cols, rows) {
    var T = this.T, P = this.data.parts, ci = 0;
    var flatH = cols.flats.map(function (sku, i) {
      var p = P[sku];
      // size over length keeps the column as narrow as EQA's port codes, which the band label spans
      var len = p.n.replace(/\s*Flat Port$/, '').replace(p.sz, '').trim();
      return '<th class="dvl-lc__col is-flat' + (i ? '' : ' dvl-lc__gstart') + '" data-col="' + ci++ + '" title="' +
        esc(p.n + (p.m ? ' \u00b7 ' + p.m : '') + (p.mat ? ' \u00b7 ' + p.mat : '')) + '">' +
        '<span class="dvl-lc__colsz">' + esc(p.sz) + '</span>' +
        (len ? '<span class="dvl-lc__collen">' + esc(len) + '</span>' : '') + '</th>';
    }).join('');
    var rings = this.data.rings || {};
    var domeH = cols.rings.map(function (r, i) {
      var sizes = [];
      h.lenses.forEach(function (l) {
        l.dome.forEach(function (sku) { if (P[sku].ring === r && sizes.indexOf(P[sku].sz) < 0) sizes.push(P[sku].sz); });
      });
      var ring = rings[r] || {};
      var under = ring.er || T.no_ring; // V0 and W0 mount straight on the housing, with no ring
      var note = ring.er ? ring.er + (ring.mm ? ' (' + ring.mm + ' mm)' : '') : T.no_ring;
      return '<th class="dvl-lc__col is-dome' + (i ? '' : ' dvl-lc__gstart') + '" data-col="' + ci++ + '" title="' +
        esc(T.dome + ' ' + r + ' \u00b7 ' + note + (sizes.length ? ' \u00b7 ' + sizes.sort().join(' / ') : '')) + '">' +
        '<span class="dvl-lc__colsz">' + esc(r) + '</span>' +
        '<span class="dvl-lc__collen">' + esc(under) + '</span></th>';
    }).join('');

    var bandCells =
      (cols.flats.length ? '<th class="dvl-lc__grp is-flat dvl-lc__gstart" colspan="' + cols.flats.length + '">' + esc(T.flat_ports) + '</th>' : '') +
      (cols.rings.length ? '<th class="dvl-lc__grp is-dome dvl-lc__gstart" colspan="' + cols.rings.length + '">' + esc(T.dome_versions) + '</th>' : '');

    var lensRows = '', portRows = '';
    rows.forEach(function (l) {
      var cj = 0, domeRings = l.dome.map(function (sku) { return P[sku].ring; }), nt = l.nt || {};
      // the catalog's note on this lens behind that port: a corner mark, the text on hover and in the panel
      var noted = function (y, key) { return y && nt[key] ? ' has-note" title="' + esc(nt[key]) : ''; };
      var fc = cols.flats.map(function (sku, i) {
        var y = l.flat.indexOf(sku) >= 0;
        // a gear listed for this housing applies to every flat port of the lens (as in EQA)
        return '<td class="dvl-lc__cell' + (y ? ' is-yes is-flat' : '') + (i ? '' : ' dvl-lc__gstart') + noted(y, sku) + '" data-col="' + cj++ + '">' +
          (y ? '<span class="dvl-lc__dot"></span>' : '') + (y && l.gear.length ? '<span class="dvl-lc__zg">\u2699</span>' : '') + '</td>';
      }).join('');
      var dc = cols.rings.map(function (r, i) {
        var y = domeRings.indexOf(r) >= 0;
        return '<td class="dvl-lc__cell' + (y ? ' is-yes is-dome' : '') + (i ? '' : ' dvl-lc__gstart') + noted(y, r) + '" data-col="' + cj++ + '">' +
          (y ? '<span class="dvl-lc__dot"></span>' : '') + (y && l.gr.indexOf(r) >= 0 ? '<span class="dvl-lc__zg">\u2699</span>' : '') + '</td>';
      }).join('');
      lensRows += '<tr data-lens="' + esc(l.n) + '"><th class="dvl-lc__lens" title="' + esc(l.n) + '">' +
        '<span class="dvl-lc__caret" aria-hidden="true"></span>' + esc(l.n) + '</th></tr>';
      portRows += '<tr data-lens="' + esc(l.n) + '">' + fc + dc + '</tr>';
    });

    // A second horizontal scrollbar, ABOVE the chart: the pane's own one sits under the last lens row,
    // ~2,700px down on a big camera, so most shoppers never learn the dome columns exist.
    return '<div class="dvl-lc__xbar" hidden><div class="dvl-lc__xbarin"></div></div>' +
      '<div class="dvl-lc__grid">' +
      '<div class="dvl-lc__floathead" aria-hidden="true" hidden></div>' +
      '<div class="dvl-lc__grip" role="separator" aria-orientation="vertical" tabindex="0" title="' + esc(T.resize) + '">' +
        '<span class="dvl-lc__gripbar" aria-hidden="true"></span></div>' +
      '<div class="dvl-lc__lenspane"><table class="dvl-lc__mx dvl-lc__mx--lens"><thead>' +
        '<tr class="dvl-lc__band"><th class="dvl-lc__lenstop"></th></tr>' +
        '<tr class="dvl-lc__cols"><th class="dvl-lc__lensh">' + esc(T.lens_port) + '</th></tr>' +
      '</thead><tbody>' + lensRows + '</tbody></table></div>' +
      '<div class="dvl-lc__ports"><table class="dvl-lc__mx dvl-lc__mx--ports"><thead>' +
        '<tr class="dvl-lc__band">' + bandCells + '</tr>' +
        '<tr class="dvl-lc__cols">' + flatH + domeH + '</tr>' +
      '</thead><tbody>' + portRows + '</tbody></table></div></div>';
  };

  // One row per lens, saying in words what it needs — no columns, so nothing scrolls sideways.
  Finder.prototype.list = function (h, rows) {
    var T = this.T, P = this.data.parts, self = this;
    var body = rows.map(function (l) {
      var bits = [];
      l.flat.forEach(function (sku) { bits.push(P[sku].n.replace(/\s*Flat Port$/, '') + ' ' + T.flat_short); });
      var rings = [];
      l.dome.forEach(function (sku) { if (rings.indexOf(P[sku].ring) < 0) rings.push(P[sku].ring); });
      if (rings.length) bits.push(rings.join(' / ') + ' ' + T.dome_short);
      if (l.gear.length) bits.push(P[l.gear[0]].n);
      return '<div class="dvl-lc__lrow" data-lens="' + esc(l.n) + '">' +
        '<button type="button" class="dvl-lc__lhead" aria-expanded="false">' +
        '<span class="dvl-lc__caret" aria-hidden="true"></span>' +
        '<span class="dvl-lc__ltext"><span class="dvl-lc__lname">' + esc(l.n) + '</span>' +
        '<span class="dvl-lc__lsum">' + bits.map(function (b) { return '<span>' + esc(b) + '</span>'; }).join('') + '</span></span>' +
        '</button><div class="dvl-lc__lbody" hidden></div></div>';
    }).join('');
    return '<div class="dvl-lc__list">' + body + '</div>';
  };

  // The top scrollbar spans exactly the ports pane (it starts after the lens column) and scrolls as far as
  // the ports table does; it is shown only when there is something off to the side.
  Finder.prototype.sizeXbar = function (grid, lensPane, ports) {
    var xbar = this.el.results.querySelector('.dvl-lc__xbar');
    if (!xbar) return;
    var over = ports.scrollWidth - ports.clientWidth > 1;
    xbar.hidden = !over;
    if (!over) return;
    var g = grid.getBoundingClientRect(), p = ports.getBoundingClientRect();
    xbar.style.marginLeft = Math.round(p.left - g.left) + 'px';
    xbar.style.width = Math.round(ports.clientWidth) + 'px';
    xbar.firstChild.style.width = ports.scrollWidth + 'px';
    xbar.scrollLeft = ports.scrollLeft;
    this.trackFloatHead();
  };

  // Match the two panes: equal row heights, the second header row stuck under the first, and one
  // vertical scroll shared between them (the lens pane carries no scrollbar of its own).
  Finder.prototype.syncGrid = function () {
    var grid = this.el.results.querySelector('.dvl-lc__grid'), self = this;
    if (!grid) return;
    var lensPane = grid.querySelector('.dvl-lc__lenspane'), ports = grid.querySelector('.dvl-lc__ports');
    var lr = lensPane.querySelector('table').rows, pr = ports.querySelector('table').rows;
    var n = Math.min(lr.length, pr.length), i;
    for (i = 0; i < n; i++) { lr[i].style.height = ''; pr[i].style.height = ''; }
    for (i = 0; i < n; i++) {
      var hgt = Math.max(lr[i].getBoundingClientRect().height, pr[i].getBoundingClientRect().height);
      lr[i].style.height = hgt + 'px';
      pr[i].style.height = hgt + 'px';
    }
    // The grip sits on the divider. Its CSS fallback is an em value against the GRID's font size,
    // while the column is sized in the CELL's smaller em — so before the first drag the two
    // disagree; place it from the measured pane instead.
    var grip = grid.querySelector('.dvl-lc__grip');
    if (grip) grip.style.left = Math.round(lensPane.getBoundingClientRect().width) - 4 + 'px';

    var bandH = pr[0].getBoundingClientRect().height;
    Array.prototype.forEach.call(grid.querySelectorAll('.dvl-lc__cols th'), function (th) { th.style.top = bandH + 'px'; });

    this.sizeDetail();

    this.buildFloatHead(grid, lensPane, ports);
    this.sizeXbar(grid, lensPane, ports);

    if (!ports.dataset.synced) {
      ports.dataset.synced = '1';
      this.wireGrip(grid, lensPane);
      ports.addEventListener('scroll', function () { self.trackFloatHead(); });
      if (!self.viewBound && self.narrow && self.narrow.addEventListener) {
        self.viewBound = true;
        self.narrow.addEventListener('change', function (e) {
          if (self.state.viewPicked) return;
          self.state.view = e.matches ? 'list' : 'grid';
          self.renderMain();
        });
      }
      if (!self.floatBound) {
        self.floatBound = true;
        var settle = null;
        window.addEventListener('scroll', function () {
          self.trackFloatHead();
          // some themes pin their own bar a moment AFTER scrolling stops (saltedline's sticky nav),
          // and nothing else would re-run this once the page is still
          clearTimeout(settle);
          settle = setTimeout(function () { self._offAt = 0; self.trackFloatHead(); }, 400);
        }, { passive: true });
      }
      ports.addEventListener('scroll', function () { lensPane.scrollTop = ports.scrollTop; });
      // the two horizontal scrollbars drive each other; assigning the value a pane already has fires no
      // scroll event, so the pair cannot ping-pong
      var xbar = this.el.results.querySelector('.dvl-lc__xbar');
      if (xbar) {
        xbar.addEventListener('scroll', function () { if (ports.scrollLeft !== xbar.scrollLeft) ports.scrollLeft = xbar.scrollLeft; }, { passive: true });
        ports.addEventListener('scroll', function () { if (xbar.scrollLeft !== ports.scrollLeft) xbar.scrollLeft = ports.scrollLeft; }, { passive: true });
      }
      // no scrollbar on the lens pane, so wheel and drag over it have to drive the ports pane
      lensPane.addEventListener('wheel', function (e) {
        var before = ports.scrollTop;
        ports.scrollTop += e.deltaY;
        if (ports.scrollTop !== before) e.preventDefault();
      }, { passive: false });
      var y0 = 0;
      lensPane.addEventListener('touchstart', function (e) { y0 = e.touches[0].clientY; }, { passive: true });
      lensPane.addEventListener('touchmove', function (e) {
        var y = e.touches[0].clientY;
        ports.scrollTop += y0 - y;
        y0 = y;
      }, { passive: true });
    }
    lensPane.scrollTop = ports.scrollTop;
  };

  /* THIS CAMERA AND THIS LENS, as a link worth sending.
   *
   * It was a link to the camera's page at first, and that was wrong: the page it points at is the
   * camera ALREADY on screen with the SAME lens open — identical down to the 25 parts shown. Only the
   * URL and the title differ. As navigation it did nothing a shopper could see.
   *
   * What is actually wanted at that moment is the address of the combination, to send to someone or
   * keep. So it copies rather than navigates, and says so. The camera page earns its keep from search,
   * where it arrives as the landing page; it was never somewhere to send a shopper who is already
   * there. */
  Finder.prototype.lensShare = function (h, name) {
    if (!h || !h.pg) return '';
    var u = this.origin() + this.base + 'pages/' + h.pg + '?lens=' + encodeURIComponent(name);
    return '<button type="button" class="dvl-lc__share" data-copy="' + esc(u) + '">' +
      esc(this.T.copy_link || 'Copy link') + '</button>';
  };

  Finder.prototype.origin = function () {
    try { return location.origin; } catch (e) { return ''; }
  };

  /* Clipboard, with the old way BEHIND it rather than instead of it. `navigator.clipboard` needs a
     secure context, a user gesture and the permission; when any of those is missing the promise
     rejects, and an early return there left the button doing nothing at all. So a rejection falls
     through to the textarea, and the label only changes once something actually copied. */
  Finder.prototype.copy = function (btn) {
    var text = btn.getAttribute('data-copy'), T = this.T;
    var done = function () {
      if (btn.classList.contains('is-done')) return;
      var was = btn.textContent;
      btn.textContent = T.link_copied || 'Link copied';
      btn.classList.add('is-done');
      setTimeout(function () { btn.textContent = was; btn.classList.remove('is-done'); }, 2000);
    };
    var byTextarea = function () {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      try { if (document.execCommand('copy')) done(); } catch (e) { /* nothing more to try */ }
      ta.remove();
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, byTextarea);
      return;
    }
    byTextarea();
  };

  // ---- a lens row's parts -----------------------------------------------------------------------------

  Finder.prototype.rowsFor = function (name) {
    return this.el.results.querySelectorAll('tr[data-lens="' + cssEsc(name) + '"], .dvl-lc__lrow[data-lens="' + cssEsc(name) + '"]');
  };

  Finder.prototype.toggleRow = function (name) {
    this.state.auto = false;
    if (this.state.open === name) { this.closePanel(); this.renderAddons(); }
    else this.openRow(name, false);
    this.writeUrl();
  };

  Finder.prototype.closePanel = function () {
    var res = this.el.results;
    Array.prototype.forEach.call(res.querySelectorAll('.dvl-lc__detailrow, .dvl-lc__spacer'), function (r) { r.remove(); });
    Array.prototype.forEach.call(res.querySelectorAll('tr.is-open, .dvl-lc__lrow.is-open'), function (r) { r.classList.remove('is-open'); });
    Array.prototype.forEach.call(res.querySelectorAll('.dvl-lc__lbody'), function (n) { n.hidden = true; n.innerHTML = ''; });
    Array.prototype.forEach.call(res.querySelectorAll('.dvl-lc__lhead'), function (n) { n.setAttribute('aria-expanded', 'false'); });
    this.state.open = '';
  };

  // The parts open INLINE, under the lens's own row. The grid is two tables, so the panel goes in
  // the ports pane (sticky to its left edge, sized to what is visible of it) and the lens pane gets
  // a blank row of the same height — otherwise every row below would sit one panel out of step.
  Finder.prototype.openRow = function (name, scrollTo) {
    var h = this.current(), self = this;
    var lens = h && h.lenses.filter(function (l) { return l.n === name; })[0];
    if (!lens) return;

    if (this.state.view === 'list') {
      var row = this.el.results.querySelector('.dvl-lc__lrow[data-lens="' + cssEsc(name) + '"]');
      if (!row) return;
      this.closePanel();
      this.state.open = name;
      row.classList.add('is-open');
      row.querySelector('.dvl-lc__lhead').setAttribute('aria-expanded', 'true');
      var box = row.querySelector('.dvl-lc__lbody');
      var jump = this.lensShare(h, name);
      box.innerHTML = this.portGroup(h, 'flat', lens.flat, lens.nt, lens) + this.portGroup(h, 'dome', lens.dome, lens.nt, lens) +
        this.gearGroup(lens) + this.addonGroup(h, lens) +
        (jump ? '<p class="dvl-lc__note">' + jump + '</p>' : '') +
        '<p class="dvl-lc__cartline" hidden></p>';
      box.hidden = false;
      this.hydrate(box);
      this.renderAddons();
      // Twice: the parts' images and prices arrive after the first scroll and shift the page under
      // it. The second pass lands on the settled layout, clear of whatever bar the theme pins up top.
      if (scrollTo) [80, 700].forEach(function (delay) { setTimeout(function () { self.scrollToRow(row); }, delay); });
      return;
    }

    var grid = this.el.results.querySelector('.dvl-lc__grid');
    if (!grid) return;
    this.closePanel();
    var sel = 'tr[data-lens="' + cssEsc(name) + '"]';
    var lensRow = grid.querySelector('.dvl-lc__mx--lens ' + sel);
    var portRow = grid.querySelector('.dvl-lc__mx--ports ' + sel);
    if (!lensRow || !portRow) return;
    this.state.open = name;
    lensRow.classList.add('is-open');
    portRow.classList.add('is-open');

    var spacer = document.createElement('tr');
    spacer.className = 'dvl-lc__spacer';
    spacer.innerHTML = '<th></th>';
    lensRow.parentNode.insertBefore(spacer, lensRow.nextSibling);

    var det = document.createElement('tr');
    det.className = 'dvl-lc__detailrow';
    det.innerHTML = '<td colspan="' + portRow.children.length + '"><div class="dvl-lc__detail">' +
      '<div class="dvl-lc__detailhead"><b>' + esc(name) + '</b>' + this.lensShare(h, name) +
      '<button type="button" class="dvl-lc__close" aria-label="' + esc(this.T.close) + '">\u2715</button></div>' +
      this.portGroup(h, 'flat', lens.flat, lens.nt, lens) + this.portGroup(h, 'dome', lens.dome, lens.nt, lens) + this.gearGroup(lens) +
      this.addonGroup(h, lens) + '<p class="dvl-lc__cartline" hidden></p>' +
      '</div></td>';
    portRow.parentNode.insertBefore(det, portRow.nextSibling);

    this.sizeDetail();
    this.syncGrid();
    this.hydrate(det);
    this.renderAddons();
    setTimeout(function () { self.syncGrid(); }, 400); // images and prices land after the first pass
    if (scrollTo) setTimeout(function () { portRow.scrollIntoView({ block: 'center', behavior: 'smooth' }); }, 60);
  };

  // the panel is inside a table far wider than the pane, so its width has to be what is VISIBLE
  Finder.prototype.sizeDetail = function () {
    var ports = this.el.results.querySelector('.dvl-lc__ports');
    var panel = this.el.results.querySelector('.dvl-lc__detail');
    if (ports && panel) panel.style.width = ports.clientWidth + 'px';
  };

  Finder.prototype.addToCart = function (btn) {
    var self = this, T = this.T;
    var tile = btn.closest('.dvl-lc__part');
    var id = tile && tile.getAttribute('data-v');
    if (!id || btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-busy');
    btn.title = T.adding;
    fetch(this.base + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { return r.ok ? r.json() : r.json().then(function (e) { throw new Error(e.description || r.status); }); })
      .then(function () {
        btn.classList.remove('is-busy');
        btn.innerHTML = ICON.done;
        btn.title = T.added;
        btn.setAttribute('aria-label', T.added);
        tile.classList.add('is-added');
        self.cartChanged(tile);
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('is-busy');
        btn.innerHTML = ICON.fail;
        btn.title = T.add_failed;
        btn.setAttribute('aria-label', T.add_failed);
      });
  };

  // Tell the theme its cart moved. Each has its own way in — Fashionopolism exposes
  // theme.ajaxCart.updateView(), Galleria the classic Shopify.onCartUpdate(cart), Prestige listens
  // for a `cart:refresh` event — and an unknown hook is simply skipped. Whatever happens, the count
  // in the header is brought up to date and our own confirmation is shown, so nothing looks stale.
  Finder.prototype.cartChanged = function (from) {
    var self = this;
    fetch(this.base + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        try {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          if (window.theme && window.theme.ajaxCart && typeof window.theme.ajaxCart.updateView === 'function') {
            window.theme.ajaxCart.updateView();
          }
          if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function' && cart) {
            window.Shopify.onCartUpdate(cart);
          }
          if (window.jQuery) window.jQuery(document).trigger('cart:ready');
        } catch (e) {}
        if (cart) self.setCartCount(cart.item_count);
      });

    // "added to your cart" goes under whichever block the button was in — the open panel, or the
    // standalone add-on block below the matrix
    var scope = (from && from.closest('.dvl-lc__detail, .dvl-lc__lbody, .dvl-lc__addonbox')) || this.el.results;
    var line = scope.querySelector('.dvl-lc__cartline');
    if (line) {
      line.innerHTML = esc(this.T.added_line) + ' <a href="' + esc(this.base) + 'cart">' + esc(this.T.view_cart) + '</a>';
      line.hidden = false;
      // the panel just grew: in the chart the lens pane's blank row has to grow with it, or every
      // row below sits out of step with its lens
      this.syncGrid();
    }
  };

  // Last resort for a header count the theme did not refresh: only touch elements that are showing
  // a bare number, so nothing else in the header can be clobbered.
  Finder.prototype.setCartCount = function (n) {
    var sel = '[class*="cart" i][class*="count" i], [id*="cart" i][id*="count" i], [data-cart-count], .cart-count, #CartCount';
    Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
      if (el.querySelector('*')) return;
      if (!/^\s*\d+\s*$/.test(el.textContent || '')) return;
      el.textContent = String(n);
    });
  };

  Finder.prototype.scrollToRow = function (row) {
    var top = row.getBoundingClientRect().top + window.scrollY - this.headerOffset() - 10;
    try { window.scrollTo({ top: top, behavior: 'smooth' }); } catch (e) { window.scrollTo(0, top); }
  };

  Finder.prototype.tile = function (h, sku, label, kitUrl) {
    var p = this.data.parts[sku] || { n: sku }, T = this.T;
    var included = h.included === sku;
    var fit = p.fit && p.fit.length ? '<span class="dvl-lc__fit">' + esc(fill(T.fits, { cams: p.fit.join(', ') })) + '</span>' : '';
    var inner = '<span class="dvl-lc__img"></span><span class="dvl-lc__ptext"><span class="dvl-lc__pname">' + esc(label || p.n) + '</span>' + fit +
      '<span class="dvl-lc__price">' + (included ? esc(T.included) : (p.h ? '' : esc(T.not_sold))) + '</span></span>';
    if (!p.h) return '<div class="dvl-lc__part is-na">' + inner + '</div>';
    // the link keeps its job (stock, depth rating, notes are on the product page); the button adds
    // this exact variant. An in-the-box port is not for sale, so it gets no button.
    var add = included || !p.v ? '' :
      '<button type="button" class="dvl-lc__add" data-add aria-label="' + esc(T.add) + '" title="' + esc(T.add) + '">' +
      ICON.add + '</button>';
    /* Build a kit AROUND THIS PORT — the one link that knows both the lens row it sits in and the
       exact port it shows. Absent where the data has no preset for the pair: no link is better than one
       that lands on an empty configurator. */
    var kitLink = kitUrl ? '<a class="dvl-lc__kitlink" href="' + esc(kitUrl) + '">' +
      esc(this.T.build_kit || 'Build a kit with this') + '</a>' : '';
    return '<div class="dvl-lc__part' + (included ? ' is-included' : '') + '" data-h="' + esc(p.h) + '" data-v="' + esc(p.v) + '"' +
      (included ? ' data-included' : '') + '>' +
      '<a class="dvl-lc__plink" href="' + esc(this.url(p.h, p.v)) + '">' + inner + '</a>' + add + kitLink + '</div>';
  };

  /* THE KIT BUILDER, ARRIVING WITH THE ANSWERS ALREADY FILLED IN.
   *
   * A cell knows the two things the kit builder is about to ask: which lens, and which port. SPO reads
   * `?spo=<base64url JSON>` on load (spo-url-sync.js, its own feature) and applies {optionName: value},
   * so the link can carry both and the shopper lands on a configured kit instead of an empty one.
   *
   * The names and values come from data.spo, baked from the live SPO export, because neither is
   * derivable: a port dropdown is named after the CHART's lens name (not SPO's own lens list, which is
   * a different set of strings), and a SKU's value text is only unique once the placeholder images are
   * discounted. Both were measured before being relied on.
   *
   * Drift fails soft. SPO leaves an option it cannot match unset, so a stale preset degrades to a plain
   * link to the kit rather than a broken page. */
  /* WHICH BODY, when the housing serves more than one.
   *
   * A7III/A7RIII, FX3/FX30, the a6xxx family: their kit builders open with a camera dropdown and
   * every lens dropdown hangs off it, so a preset without the camera sets nothing at all. The camera
   * cannot be guessed — it is required and shows on the cart as "For Camera" — so the chart asks,
   * once per housing, and carries the answer. `body()` is that answer, or '' until it is given. */
  Finder.prototype.cameraAsk = function (h) {
    var S = this.data.spo;
    return (h && S && S.camEl && S.camEl[h.id]) || null;
  };

  /* Defaults to the first body the kit builder lists. The shopper still sees it named on the product
     page as "For Camera" before adding anything to the cart, and can change it there or here, so the
     default is a starting point rather than a decision made for them. */
  /* The body is the camera the rail is showing. It used to be a question in the lens panel, because
     the rail listed housings and a housing can serve several bodies; the rail lists cameras now, so
     the answer is already given. The kit builder's own first value stands in when the two name the
     same camera differently. */
  Finder.prototype.body = function (h) {
    var ask = this.cameraAsk(h);
    if (!ask) return '';
    var here = this.currentCam();
    if (here && ask.vals.indexOf(here.cam) >= 0) return here.cam;
    return ask.vals[0] || '';
  };

  /** The camera key and value a preset must carry here, or nothing when none is needed. */
  Finder.prototype.camPreset = function (h, preset) {
    var ask = this.cameraAsk(h);
    if (!ask) return true;              // single-body housing: nothing to ask
    var chosen = this.body(h);
    if (!chosen) return false;          // asked and unanswered: a preset would apply to nothing
    preset[ask.el] = chosen;
    return true;
  };


  /* Re-open the panel rather than re-render the table: every link in it carries the body, so they all
     have to be rebuilt, and the shopper must not lose the row they were reading. */
  Finder.prototype.pickBody = function (h, value) {
    if (!h || !value) return;
    this.state.body = this.state.body || {};
    this.state.body[h.id] = value;
    var open = this.state.open;
    if (open) { this.closePanel(); this.openRow(open, false); } else this.renderMain();
  };

  Finder.prototype.kitUrl = function (h, lensName, kind, sku) {
    var S = this.data.spo, body = h.body && h.body[0];
    if (!S || !body || !lensName) return '';
    var el = S.lensEl && S.lensEl[h.id];
    if (!el) return '';
    var preset = {};
    /* An unanswered body question costs the link its PRESET, never the link. Returning nothing here
       made the "Build a kit with this" links vanish on every shared housing, which reads as the
       feature being broken rather than as a question waiting to be answered. */
    if (!this.camPreset(h, preset)) return this.url(body.h);
    preset[el] = lensName;
    var pe = S.portEl && S.portEl[lensName], pv = S.val && S.val[String(sku).toUpperCase()];
    if (pe && pe[kind] && pv) preset[pe[kind]] = pv;
    var param;
    try {
      param = btoa(unescape(encodeURIComponent(JSON.stringify(preset))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return ''; }
    var u = this.url(body.h);
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'spo=' + param;
  };

  /* The bundle's own link, with the lens for this row already answered.
   *
   * A bundle is a DIFFERENT product from the body the port tiles link to: it has its own option set,
   * the port is already fixed by the bundle, and the only question left is the lens. So the preset here
   * is lens-only, and it uses that product's element name (`spo.bundleEl`), not the body's — the card
   * was otherwise the one link in the group that arrived unconfigured.
   *
   * A DOME bundle asks the lens once (the ring decides the list), so one card carries it. A FLAT
   * bundle sells the port LENGTH as a Shopify variant and asks the lens once per length, each dropdown
   * shown only for its own variant titles — so its card has to open the variant as well, which makes
   * it a card per port rather than per bundle. `sku` selects that case via `spo.bundlePort`.
   *
   * Opening a variant is not an extra assumption: the product already opens on an arbitrary one
   * (Orange / Long), so this swaps that for the length the shopper actually clicked. */
  /* The variant of the bundle that contains this port, or nothing. Shared by the link and the card,
     so the card shows the price and the photo of the very kit the link opens. */
  Finder.prototype.bundleVariant = function (handle, sku) {
    var S = this.data.spo;
    var perPort = sku && S && S.bundlePort && S.bundlePort[handle];
    var hit = perPort && perPort[String(sku).toUpperCase()];
    return hit ? hit.v : '';
  };

  /* The same kit in every colour it is made in. Colour is the one thing the chart cannot infer from
     the cell — a lens and a port say nothing about whether the housing is black, orange or white —
     so the card offers all of them rather than deciding. */
  Finder.prototype.bundleColours = function (handle, sku) {
    var S = this.data.spo;
    var perPort = sku && S && S.bundlePort && S.bundlePort[handle];
    var hit = perPort && perPort[String(sku).toUpperCase()];
    return (hit && hit.cv) || [];
  };

  Finder.prototype.bundleUrl = function (handle, lensName, sku, colour) {
    var u = this.url(handle), S = this.data.spo, el, variant;
    if (!S || !lensName) return u;
    var perPort = sku && S.bundlePort && S.bundlePort[handle];
    var hit = perPort && perPort[String(sku).toUpperCase()];
    if (hit) { el = hit.el; variant = colour || hit.v; }
    else if (!sku) el = S.bundleEl && S.bundleEl[handle];
    // the variant goes on either way: it opens the dome or the length this cell is about
    if (variant) u += (u.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + encodeURIComponent(variant);
    if (!el || !lensName) return u;
    var preset = {};
    // a housing that asks which body carries no preset until that is answered — see camPreset
    if (!this.camPreset(this.current(), preset)) return u;
    preset[el] = lensName;
    var param;
    try {
      param = btoa(unescape(encodeURIComponent(JSON.stringify(preset))))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    } catch (e) { return u; }
    return u + (u.indexOf('?') >= 0 ? '&' : '?') + 'spo=' + param;
  };

  /* A preconfigured kit for this port group, as a card in the same grid rather than a line of text
     underneath it: the whole thing beside its parts, which is the comparison a shopper is making.
     These kits carry 18 variants (colour x size x material), so there is no single thing to add to a
     cart — the card links through to the kit, where that choice belongs. */
  Finder.prototype.kitCard = function (handle, sub, lensName, sku) {
    var self = this, T = this.T, variant = this.bundleVariant(handle, sku);
    var colours = this.bundleColours(handle, sku);

    /* One link per colour, as a rail down the side of the card. The card is therefore a DIV: an
       anchor cannot contain anchors, and these have to be real links so each carries its own variant
       and the same lens preset. Which colours are in stock is only known once the product has loaded,
       so hydrate fills the photos and removes the ones that are not for sale. */
    var rail = colours.length > 1
      ? '<span class="dvl-lc__swatches">' + colours.map(function (cv) {
          return '<a class="dvl-lc__swatch" data-sv="' + esc(cv) + '" href="' +
            esc(self.bundleUrl(handle, lensName, sku, cv)) + '"></a>';
        }).join('') + '</span>'
      : '<span class="dvl-lc__img"></span>';

    /* data-v hands hydrate the variant, which is what makes the card show THIS kit: its own price and
       its own photo. partImage already steps over the "-collection-" composites — several of these
       variants carry one as their variant image — and falls back to a real photo of the part. */
    return '<div class="dvl-lc__part dvl-lc__kit" data-h="' + esc(handle) + '"' +
      (variant ? ' data-v="' + esc(variant) + '"' : '') + '>' +
      rail +
      '<a class="dvl-lc__ptext dvl-lc__kitmain" href="' + esc(this.bundleUrl(handle, lensName, sku)) + '">' +
      '<span class="dvl-lc__kitlabel">' + esc(T.kit_label || 'Complete kit') + '</span>' +
      '<span class="dvl-lc__pname dvl-lc__ptitle"></span>' +
      (sub ? '<span class="dvl-lc__fit">' + esc(sub) + '</span>' : '') +
      '<span class="dvl-lc__price"></span></a></div>';
  };

  Finder.prototype.portGroup = function (h, kind, skus, nt, lens) {
    if (!skus || !skus.length) return '';
    var T = this.T, P = this.data.parts, self = this;
    // notes are keyed by flat SKU or dome RING; one line per distinct text, naming the ports only when
    // they say different things (or when only some of them say it)
    var notes = [], keys = [];
    skus.forEach(function (sku) {
      var k = kind === 'dome' ? (P[sku] || {}).ring : sku;
      if (k && keys.indexOf(k) < 0) keys.push(k);
    });
    keys.forEach(function (k) {
      var t = nt && nt[k];
      if (!t) return;
      var hit = notes.filter(function (x) { return x.t === t; })[0];
      if (hit) hit.k.push(k); else notes.push({ t: t, k: [k] });
    });
    var label = function (k) { return kind === 'dome' ? k : (P[k] || {}).n || k; };
    var noteHtml = notes.map(function (x) {
      var all = x.k.length === keys.length;
      return '<p class="dvl-lc__pnote"><b>' + esc(all ? (T.port_note || 'Note') : x.k.map(label).join(', ')) + ':</b> ' + esc(x.t) + '</p>';
    }).join('');
    /* One card per bundle — except where the bundle asks the lens per port length, which is a card per
       PORT, because only the variant for that length shows the right question. The port names them
       apart, so a lens that fits a 4" Medium and a 4" Long offers both as separate kits. */
    var rings = [], byPort = {}, loose = [], seen = {};
    var BP = (this.data.spo && this.data.spo.bundlePort) || {};
    skus.forEach(function (sku) {
      var p = P[sku] || {};
      if (p.ring && rings.indexOf(p.ring) < 0) rings.push(p.ring);
      var b = h.bundles && h.bundles[kind === 'dome' ? p.ring : p.lc];
      if (!b) return;
      var perBundle = BP[b];
      var per = perBundle && perBundle[String(sku).toUpperCase()];
      // A per-port bundle lists exactly the ports it sells, so a port missing from it is one this kit
      // cannot contain (the 4" bundle has no Pancake). Showing its card would offer an impossible kit.
      if (perBundle && !per) return;
      var key = per ? b + '|' + sku : b;
      if (seen[key]) return;
      seen[key] = 1;
      if (per) byPort[String(sku).toUpperCase()] = { h: b, sku: sku };
      else loose.push({ h: b, sku: null });
    });
    var sub = [];
    if (skus.length > 1) sub.push(T.pick_one);
    if (rings.length) {
      var R = this.data.rings || {};
      sub.push(rings.map(function (r) { return R[r] && R[r].er ? r + ' (' + R[r].er + ')' : r; }).join(' / ') + ' ' + T.ring);
    }
    return '<div class="dvl-lc__group"><h4 class="dvl-lc__gtitle">' +
      '<span class="dvl-lc__lg ' + (kind === 'dome' ? 'is-dome' : 'is-flat') + '"></span>' + esc(T[kind]) +
      (sub.length ? ' <span class="dvl-lc__muted">· ' + esc(sub.join(' · ')) + '</span>' : '') + '</h4>' +
      '<div class="dvl-lc__parts">' +
      /* The port and the kit that contains it are one choice seen two ways \u2014 buy the part, or buy the
         whole thing \u2014 so they share a cell: the port, and the complete kit under it. The kit drops the
         port's name there, since the tile above already says it. A port the bundles cannot supply, and
         a bundle that names no single port, each stand on their own. */
      skus.map(function (sku) {
        var tile = self.tile(h, sku, null, lens ? self.kitUrl(h, lens.n, kind, sku) : '');
        var c = byPort[String(sku).toUpperCase()];
        if (!c) return tile;
        return '<div class="dvl-lc__pair">' + tile + self.kitCard(c.h, '', lens && lens.n, c.sku) + '</div>';
      }).join('') +
      loose.map(function (c) {
        return self.kitCard(c.h, sub.length ? sub.join(' \u00b7 ') : '', lens && lens.n, c.sku);
      }).join('') +
      '</div>' + noteHtml + '</div>';
  };

  Finder.prototype.gearGroup = function (lens) {
    if (!lens.gear.length) return '';
    var T = this.T, P = this.data.parts, h = this.current(), self = this;
    // the kit builder lives on the housing's own page, so the note links straight to it
    var body = h.body && h.body[0];
    /* The gear comes free with a port on every housing but one — `spo.gearPaid` names the exceptions,
       read from the kit builder's own wording rather than hard-coded here. A housing not in the list
       keeps the free note, which is what this said before the exception was known. */
    var paid = ((this.data.spo || {}).gearPaid || []).indexOf(h.id) >= 0;
    var tpl = paid ? (T.gear_note_paid || T.gear_note) : T.gear_note;
    var note = fill(tpl, { url: body ? esc(this.url(body.h)) : esc(this.base) });
    return '<div class="dvl-lc__group"><h4 class="dvl-lc__gtitle"><span class="dvl-lc__zg">⚙</span> ' + esc(P[lens.gear[0]].n) + '</h4>' +
      '<div class="dvl-lc__parts">' + lens.gear.map(function (sku) {
        return self.tile(h, sku, fill(T.gear_for, { type: P[sku].n, lens: lens.n }));
      }).join('') + '</div><p class="dvl-lc__note">' + note + '</p></div>';
  };

  // The catalog links accessories to a HOUSING and to a PORT, never to a lens, so this is one set
  // per housing — shown once, under the matrix, and moved into whichever lens panel is open. With a
  // lens open the ports on that row bring their own (a cover and a spare sphere per dome size), which
  // is the only part of the set that changes from lens to lens.
  Finder.prototype.addonGroup = function (h, lens) {
    var T = this.T, P = this.data.parts, self = this, skus = [];
    var push = function (list) {
      (list || []).forEach(function (sku) {
        var p = P[sku];
        if (p && p.h && skus.indexOf(sku) < 0) skus.push(sku); // silently drop what this store doesn't sell
      });
    };
    var ports = lens || h.fixed;
    if (ports) (ports.flat || []).concat(ports.dome || []).forEach(function (sku) { push((P[sku] || {}).ad); });
    push(h.addons);
    if (!skus.length) return '';
    return '<div class="dvl-lc__group dvl-lc__group--addons"><h4 class="dvl-lc__gtitle">' +
      '<span class="dvl-lc__lg is-addon"></span>' + esc(T.addons) +
      ' <span class="dvl-lc__muted">\u00b7 ' + esc(lens ? T.addons_lens : T.addons_housing) + '</span></h4>' +
      '<div class="dvl-lc__parts">' + skus.map(function (sku) { return self.tile(h, sku); }).join('') + '</div></div>';
  };

  // Where the block lives depends on what is open: with a lens panel showing it is rendered inside
  // that panel, so the standalone one under the matrix stands down.
  Finder.prototype.renderAddons = function () {
    var box = this.el.addonbox;
    if (!box) return;
    var h = this.current();
    box.innerHTML = h && !this.state.open ? this.addonGroup(h, null) + '<p class="dvl-lc__cartline" hidden></p>' : '';
    box.hidden = !box.innerHTML;
    if (!box.hidden) this.hydrate(box);
  };

  // ---- boot -------------------------------------------------------------------------------------------

  function init(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-dvl-lens-compat]'), function (root) {
      if (root.__dvlLc) return;
      root.__dvlLc = new Finder(root);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
